import React, { PropsWithChildren } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Student } from "@/__generated__/graphql";
import { useLocale } from "@/hooks/useLocale";
import { NameAndAvatar } from "@/components/StudentListWrapper/Grid/NameAndAvatar";

interface StudentBarProps {
  students: Student[];
  header?: boolean;
  emptyHeader?: boolean;
  numberToShow?: number;
  totalCount?: number;
}

const EXAMPLE_STUDENT_COUNT = 6;

export function StudentBar({
  students,
  children,
  header = true,
  numberToShow = EXAMPLE_STUDENT_COUNT,
  totalCount = students.length,
}: PropsWithChildren<StudentBarProps>) {
  if (!students.length) {
    return <EmptyStudentBar header={header} />;
  }

  return (
    <div className="flex flex-col gap-2">
      {header && (
        <p data-testid="found-student-header">
          <Trans
            i18nKey="bulkMessaging.studentBar.foundStudents"
            count={totalCount}
            values={{ count: totalCount.toLocaleString() }}
          >
            Found <strong className="font-medium"></strong> Students
          </Trans>
        </p>
      )}
      <div className="flex items-center gap-6">
        <StudentImages
          students={students}
          totalCount={totalCount}
          numberToShow={numberToShow}
        />
        {children}
      </div>
    </div>
  );
}

interface StudentImagesProps {
  students: Student[];
  numberToShow: number;
  totalCount: number;
}

function StudentImages({
  students,
  totalCount,
  numberToShow,
}: StudentImagesProps) {
  const exampleStudents = students.slice(0, numberToShow);

  return (
    <div className="flex items-center gap-4">
      <div className="flex">
        {exampleStudents.map(({ firstName, lastName, profilePhoto }, i) => {
          const { url } = profilePhoto ?? {};

          return (
            <div
              key={i}
              className="relative -mr-2 rounded-full border-2 border-background"
            >
              <NameAndAvatar
                key={i}
                imageUrl={{ url: url ?? "" }}
                alt={`${firstName} ${lastName}`}
                avatarSize="size-12"
              />
            </div>
          );
        })}
      </div>
      <ExtraStudents totalCount={totalCount} numberToShow={numberToShow} />
    </div>
  );
}

interface ExtraStudentsProps {
  numberToShow: number;
  totalCount: number;
}

function ExtraStudents({ totalCount, numberToShow }: ExtraStudentsProps) {
  const { t } = useTranslation();
  const locale = useLocale();

  if (totalCount > numberToShow) {
    return (
      <div className="flex gap-1 text-sm text-foreground">
        <strong className="font-bold">
          +{Intl.NumberFormat(locale).format(totalCount - numberToShow)}
        </strong>
        <p>{t("bulkMessaging.studentBar.extraStudents")}</p>
      </div>
    );
  }
}

export function EmptyStudentBar({ header = true }: { header?: boolean }) {
  const { t } = useTranslation();

  return (
    <div className="flex h-20 flex-col gap-2">
      {header && <p>{t("bulkMessaging.studentBar.didNotFindAny")}</p>}
      <div className="flex items-center gap-2">
        <div className="flex">
          <span className="-mr-2 size-12 place-content-center rounded-full border-2 border-background bg-gray-50 text-center text-3xl">
            🙁
          </span>
          <span className="-mr-2 size-12 place-content-center rounded-full border-2 border-background bg-gray-50 text-center text-3xl">
            😿
          </span>
          <span className="-mr-2 size-12 place-content-center rounded-full border-2 border-background bg-gray-50 text-center text-3xl">
            😥
          </span>
          <span className="-mr-2 size-12 place-content-center rounded-full border-2 border-background bg-gray-50 text-center text-3xl">
            😩
          </span>
        </div>
      </div>
    </div>
  );
}
